import React, { useState, useEffect, createContext, useContext } from 'react';
import { Alert, Snackbar, Slide } from '@mui/material';
import { useSnackbar } from 'notistack';

const NetworkStatusContext = createContext();

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error('useNetworkStatus must be used within a NetworkStatusProvider');
  }
  return context;
};

function TransitionUp(props) {
  return <Slide {...props} direction="up" />;
}

const NetworkStatusProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineAlert(false);
      enqueueSnackbar('You\'re back online!', { 
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        TransitionComponent: TransitionUp
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
   
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enqueueSnackbar]);

  const handleCloseOfflineAlert = () => {
    setShowOfflineAlert(false);
  };

  const value = {
    isOnline,
    showOfflineAlert,
    handleCloseOfflineAlert
  };

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
      
      {/* Persistent offline banner */}
      <Snackbar
        open={showOfflineAlert && !isOnline}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={TransitionUp}
      >
        <Alert
          severity="warning"
          onClose={handleCloseOfflineAlert}
          sx={{
            width: '100%',
            backgroundColor: '#ff9800',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
        >
          You're offline. Changes won't be saved until you're back online.
        </Alert>
      </Snackbar>
    </NetworkStatusContext.Provider>
  );
};

export default NetworkStatusProvider;
